/**
 * Time of Birth — Hours / Minutes boxes + an AM/PM toggle. Optional by default
 * (isBirthTimeMandatory is false today); blank → submitted as null. No "I don't
 * know my time" checkbox — that's a Figma-only element Android doesn't have.
 */
import { FocusEvent } from 'react'
import { FieldError, FieldLabel } from './FieldLabel'

interface Props {
  hours: string
  minutes: string
  isAm: boolean
  required: boolean
  onChange: (field: 'hours' | 'minutes' | 'isAm', value: string | boolean) => void
  /** Fired when focus leaves the whole time group; range/half-filled check runs then. */
  onBlur?: () => void
  error?: string
}

// Digits only, max 2 chars — no clamping. Out-of-range values surface as an inline
// error on blur/submit (we don't silently rewrite what the user typed).
const twoDigits = (s: string) => s.replace(/\D/g, '').slice(0, 2)

const boxClass =
  'min-w-0 flex-1 rounded-xl border border-textSeparator bg-transparent px-4 py-3 text-center text-labelLarge text-textPrimary placeholder:text-textDisabled focus:border-textAccentPrimary focus:outline-none'

export function TimeOfBirthField({ hours, minutes, isAm, required, onChange, onBlur, error }: Props) {
  const handleGroupBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onBlur?.()
  }

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel required={required}>Time of Birth</FieldLabel>
      <div className="flex items-center gap-2" onBlur={handleGroupBlur}>
        <input
          type="text"
          inputMode="numeric"
          value={hours}
          placeholder="Hours"
          aria-label="Hours"
          onChange={(e) => onChange('hours', twoDigits(e.target.value))}
          className={boxClass}
        />
        <span className="text-labelLarge text-textSecondary">:</span>
        <input
          type="text"
          inputMode="numeric"
          value={minutes}
          placeholder="Minutes"
          aria-label="Minutes"
          onChange={(e) => onChange('minutes', twoDigits(e.target.value))}
          className={boxClass}
        />
        <div className="flex shrink-0 overflow-hidden rounded-xl border border-textSeparator">
          {[
            { label: 'AM', am: true },
            { label: 'PM', am: false },
          ].map((opt) => {
            const active = isAm === opt.am
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => onChange('isAm', opt.am)}
                aria-pressed={active}
                className={`px-3 py-3 text-labelSmall ${
                  active ? 'bg-surfaceOne text-textPrimary' : 'text-textSecondary'
                }`}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      </div>
      <FieldError message={error} />
    </div>
  )
}
